<script setup lang="ts">
import { computed, onUnmounted, watch } from "vue";
import ObservationsMenu from "./ObservationsMenu.vue";
import ObservationsPartsMenu from "./ObservationsPartsMenu.vue";
import AnalysisMenu from "../analysis/AnalysisMenu.vue";
import {
  activeTab,
  availableDrawerTabs,
  isAnalysisTab,
  resolveDrawerTab,
  setActiveTab,
  type DrawerTabsContext,
  type ObservationsDrawerTab,
} from "@/stores/ObservationsPanelState";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { provideDrawingDiagnosticLink } from "@/composable/useDrawingDiagnosticLink";
import { useGetFieldObservations } from "@/stores/queries/field-observations.query";

/**
 * Volet « Observations » de l'outil dessin : un seul volet pour les
 * observations réellement créées (`ObservationsMenu`), leur classement par
 * partie d'ouvrage (`ObservationsPartsMenu`) et les groupes détectés par
 * l'analyse IA (`AnalysisMenu`). Les onglets vivent dans
 * `ObservationsDrawerTabs`, rendus par chaque panneau sous le titre du volet.
 */

const props = defineProps<{
  drawingId: number;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const analysis = usePlanAnalysisState();

// Le lien plan ↔ ouvrage est chargé une fois pour le volet : changer
// d'onglet remonte un panneau, sans relire le plan ni attendre le serveur.
const { drawing, diagnosticId, isLoadingDrawing } =
  provideDrawingDiagnosticLink(props.drawingId);

// « Validé » d'un groupe d'analyse = son observation existe encore : les
// observations de l'ouvrage, relues du serveur à chaque changement, tiennent
// ce calcul à jour pour tous les onglets (cf. `statusOf` du store).
const { data: observations } = useGetFieldObservations(diagnosticId);
watch(
  observations,
  (list) => {
    analysis.setKnownObservations(
      list
        ? list.flatMap((observation) =>
            observation.localId ? [observation.localId] : [],
          )
        : null,
    );
  },
  { immediate: true },
);

// Tant que le plan n'a pas été lu en local (quelques ms), il est tenu pour
// lié : rouvrir le volet sur l'onglet Parties ne repasse pas par
// Observations. Ensuite la copie locale fait foi sans attendre le serveur —
// sur un plan sans ouvrage, la barre d'onglets ne s'affiche pas le temps de
// la requête.
const tabsContext = computed<DrawerTabsContext>(() => ({
  hasAnalysis: analysis.hasAnalysis,
  isLinked:
    diagnosticId.value > 0 || (isLoadingDrawing.value && !drawing.value),
}));
const tabs = computed(() => availableDrawerTabs(tabsContext.value));
const tab = computed<ObservationsDrawerTab>(() =>
  resolveDrawerTab(activeTab.value, tabsContext.value),
);

watch(
  tab,
  (current) => {
    // L'onglet mémorisé n'est plus proposé (analyse effacée, plan sans
    // ouvrage) : la mémoire suit l'onglet affiché, sinon la barre n'en
    // montrerait aucun d'actif.
    if (current !== activeTab.value) setActiveTab(current);
    // Les cadres violets des groupes ne s'affichent sur le plan que devant
    // l'onglet IA ; les deux autres onglets montrent les cadres bleus.
    analysis.isPanelOpen = isAnalysisTab(current);
  },
  { immediate: true },
);

onUnmounted(() => {
  analysis.isPanelOpen = false;
});
</script>

<template>
  <ObservationsMenu
    v-if="tab === 'observations'"
    :drawing-id="drawingId"
    :tabs="tabs"
    @close="$emit('close')"
  />
  <ObservationsPartsMenu
    v-else-if="tab === 'parts'"
    :drawing-id="drawingId"
    :tabs="tabs"
    @close="$emit('close')"
  />
  <AnalysisMenu
    v-else
    :drawing-id="drawingId"
    :tabs="tabs"
    @close="$emit('close')"
  />
</template>
